import { useEffect, useRef } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Outlet, ScrollRestoration, useLocation, useNavigate } from "react-router-dom";
import { ErrorPanel, Loading } from "@/components/atoms";
import { ClientPortalClientBanner, ConfigProvider, Head, PortalRefreshTour } from "@/components/organisms";
import { BulkImportReportModal } from "@/components/organisms/BulkBooking/BulkImportReportModal";
import { ClientHeader } from "@/components/organisms/ClientHeader";
import { readStashedGuestDraft } from "@/components/pages/Guest/guestDraftStash";
import { SubscriptionExpired } from "@/components/pages/SubscriptionExpired/SubscriptionExpired";
import { useBranding, useSiteConfig } from "@/hooks";
import { useSetupClientAccount } from "./hooks/useSetupClientAccount";

const ClientBranding = ({ children }: React.PropsWithChildren) => {
  const { branding } = useSiteConfig();
  useBranding(branding);
  return <>{children}</>;
};

export const ClientPortal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const resumedRef = useRef(false);

  // Guest→login resume: a guest who hit "you already have an account" (email-check gate or the
  // create-time ACCOUNT_EXISTS 409) stashed their in-progress booking before being sent to log in. Once
  // they're authenticated and in the client portal, send them to the booking page — ClientBooking
  // restores the stashed draft there (and clears it), so they finish the trip as themselves. One-shot,
  // and skipped if they already landed on the booking page.
  useEffect(() => {
    if (resumedRef.current) return;
    if (readStashedGuestDraft() && !location.pathname.includes("/client/booking")) {
      resumedRef.current = true;
      navigate("/client/booking", { replace: true });
    }
  }, [navigate, location.pathname]);

  // The recovery flow (JoinOperator) draws its own GeneralHeader/GeneralFooter full-screen, so skip
  // the portal chrome (ClientHeader + <main>) on that route to avoid double headers. Account setup
  // still runs (ClientAccountProvider) so profile/activeAccount are populated.
  const isJoinOperator = location.pathname.endsWith("/join-operator");

  // The Manage-Client takeover (DEV-408) draws its own full-screen chrome (blue banner + logo/tabs +
  // Stop Managing), so skip the portal chrome (ClientHeader + <main>) on that route and any nested
  // child to avoid double headers — matching the JoinOperator precedent. Account setup still runs.
  const isManageClient = /\/my-clients\/[^/]+\/manage(\/|$)/.test(location.pathname);
  const isBareChrome = isJoinOperator || isManageClient;

  return (
    <ConfigProvider>
      <ClientBranding>
        <ClientAccountProvider>
          <Head />
          <ScrollRestoration />
          <SubscriptionExpired />
          {isBareChrome ? (
            <Outlet />
          ) : (
            <div className="flex h-screen flex-col">
              {/* Above the header so it reads as a platform-wide notice rather than page content.
                  Skipped on the bare-chrome routes above, which draw their own full-screen shell. */}
              <ClientPortalClientBanner />
              <ClientHeader />
              <ErrorBoundary fallback={<ErrorPanel messages={["Something went wrong."]} />}>
                <main className="flex flex-1 flex-col overflow-auto bg-neutral-surface-gray">
                  <Outlet />
                </main>
              </ErrorBoundary>
            </div>
          )}
          {/* Mounted at the layout, not in Book a Trip: the completion snackbar waits to be dismissed
              and so outlives any one page, and this is what it opens (DEV-1215). */}
          <BulkImportReportModal />
          {/* Picks the assistant or client deck from the account type, self-gates on already-dismissed,
              and defers to SubscriptionExpired and the import report rather than stacking on them.
              Client portal only — see PortalRefreshTour. Skipped on the bare-chrome routes for the same
              reason as the banner: an announcement over a full-screen takeover or the recovery flow
              interrupts a task. Deferring costs nothing — nothing is marked seen, so it shows on the
              next ordinary page. */}
          {!isBareChrome && <PortalRefreshTour />}
        </ClientAccountProvider>
      </ClientBranding>
    </ConfigProvider>
  );
};

const ClientAccountProvider = ({ children }: React.PropsWithChildren) => {
  const { isReady } = useSetupClientAccount();
  const hasLoadedOnce = useRef(false);

  if (isReady) {
    hasLoadedOnce.current = true;
  }

  if (!isReady && !hasLoadedOnce.current) {
    return <Loading />;
  }

  return <>{children}</>;
};
